/**
 * O regime da tarifa no idioma da tela (#4090).
 *
 * O `boardDescription` chega do fornecedor em pt-BR e entra em frases já
 * traduzidas — "Terms for the … rate", a linha da tarifa, o checkout. O
 * vocabulário é fechado: a amostra de prod é "Café da manhã", "Sem café da
 * manhã" em grafias diferentes por fornecedor e as vantagens da Expedia em
 * lista separada por vírgula. Catálogo resolve; tradução automática não entra.
 *
 * Só traduz quando reconhece TODOS os termos — meia tradução ("Free Wi-Fi,
 * Frigobar") lê pior que o original. Em pt-BR o texto do fornecedor sai como
 * veio.
 */
import { genUiT, resolveGenUiLocale, type GenUiKey } from '../../shared/i18n_core.js';
import { titleTokens } from './named_hotel.js';

type BoardKey = Extract<GenUiKey, `hotel.board.${string}`>;

/** Chave = palavras sem acento e sem caixa (`titleTokens`), juntas por espaço.
 *  `Map`, não objeto: "constructor" acharia a propriedade herdada do `Object`. */
const BOARD_KEY_BY_TERM: ReadonlyMap<string, BoardKey> = new Map([
  ['apenas quarto', 'hotel.board.roomOnly'],
  ['cafe da manha', 'hotel.board.breakfast'],
  ['com cafe da manha', 'hotel.board.breakfast'],
  ['cafe da manha incluso', 'hotel.board.breakfast'],
  ['sem cafe da manha', 'hotel.board.noBreakfast'],
  ['sem cafe', 'hotel.board.noBreakfast'],
  ['cafe da manha continental', 'hotel.board.continentalBreakfast'],
  ['meia pensao', 'hotel.board.halfBoard'],
  ['pensao completa', 'hotel.board.fullBoard'],
  ['tudo incluido', 'hotel.board.allInclusive'],
  ['wi fi gratis', 'hotel.board.freeWifi'],
  ['internet de alta velocidade gratis', 'hotel.board.freeHighSpeedInternet'],
  ['estacionamento gratis sem manobrista', 'hotel.board.freeSelfParking'],
  ['estacionamento com manobrista gratis', 'hotel.board.freeValetParking'],
]);

const BREAKFAST_FOR = /^cafe da manha para (\d+) pessoas?$/;

function termLabel(term: string, locale: string): string | undefined {
  const normalized = titleTokens(term).join(' ');
  const key = BOARD_KEY_BY_TERM.get(normalized);
  if (key) return genUiT(locale, key);
  const count = BREAKFAST_FOR.exec(normalized)?.[1];
  return count ? genUiT(locale, 'hotel.board.breakfastFor', { count }) : undefined;
}

export function localizedBoardLabel(text: string, locale: string): string;
export function localizedBoardLabel(text: string | undefined, locale: string): string | undefined;
export function localizedBoardLabel(text: string | undefined, locale: string): string | undefined {
  if (!text || resolveGenUiLocale(locale) === 'pt-BR') return text;
  const labels = text.split(',').map((term) => termLabel(term, locale));
  return labels.every((label) => label !== undefined) ? labels.join(', ') : text;
}
